import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Input, Button } from 'shared-components';
import LoginService, { RequestResetErrorCode, ResetPasswordErrorCode } from '../../Services/LoginService';
import AccountService from '../../Services/AccountService';
import { useLoginState } from './LoginContext';
import { LoginCard } from './LoginCard';
import Loading from '../../Abstracts/Components/Loading';

const requestResetErrorCopy: Record<RequestResetErrorCode, string> = {
	rate_limited: 'You have requested too many resets. Please check your inbox or try again later.',
	duplicate_email: 'We found multiple accounts with that email. Please contact our office to reset your password.',
};

const resetPasswordErrorCopy: Record<ResetPasswordErrorCode, string> = {
	invalid_token: 'This reset link is invalid. Please request a new one.',
	expired_token: 'This reset link has expired. Please request a new one.',
	rate_limited: 'Too many attempts. Please wait a moment before trying again.',
};

export const ResetPasswordRouter = () => {
	const location = useLocation();
	const token = new URLSearchParams(location.search).get('token');
	return token ? <ResetPasswordForm token={token} /> : <ResetRequestForm />;
};

const ResetRequestForm = () => {
	const [email, setEmail] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [resultCopy, setResultCopy] = useState<string | null>(null);
	const [errorCode, setErrorCode] = useState<RequestResetErrorCode | null>(null);

	const onSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setSubmitting(true);
		setResultCopy(null);
		setErrorCode(null);
		try {
			const response = await LoginService.requestPasswordReset(email.trim());
			if ('error' in response) {
				setErrorCode(response.code);
			} else {
				setResultCopy("If an account exists for that email we've sent instructions to reset your password.");
				setSubmitted(true);
			}
		} catch (err) {
			console.error(err);
			toast.error('Unable to submit password reset right now.');
		} finally {
			setSubmitting(false);
		}
	};

	const errorMessage = errorCode ? requestResetErrorCopy[errorCode] : null;

	return (
		<LoginCard>
			<h1 className="login-title">Reset password</h1>
			<p className="login-description">
				Enter your email address and we&apos;ll send you instructions to reset your password.
			</p>
			<form className="login-form" onSubmit={onSubmit}>
				<Input
					id="reset-email"
					label="Email"
					type="email"
					value={email}
					onChange={(value) => setEmail(value)}
					className="login-field"
					inputProps={{ autoComplete: 'email', required: true }}
				/>
				{errorMessage ? (
					<p className="login-error" role="alert">
						{errorMessage}
					</p>
				) : null}
				{resultCopy ? (
					<p className="login-success" role="status">
						{resultCopy}
					</p>
				) : null}
				<Button
					className="login-button"
					color="primary"
					size="md"
					variant="contained"
					type="submit"
					disabled={submitting || submitted}
				>
					{submitting ? 'Sending...' : 'Send reset link'}
				</Button>
			</form>
			<div className="login-footer">
				<Link to="/" className="login-link">
					Return to sign in
				</Link>
			</div>
		</LoginCard>
	);
};

const ResetPasswordForm = ({ token }: { token: string }) => {
	const { setFatalError } = useLoginState();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [errorCode, setErrorCode] = useState<ResetPasswordErrorCode | null>(null);
	const [formError, setFormError] = useState<string | null>(null);
	const [redirecting, setRedirecting] = useState(false);
	const [validating, setValidating] = useState(true);
	const [tokenValid, setTokenValid] = useState<boolean | null>(null);
	const passwordRequirementCopy = AccountService.getPasswordRequirementMessage();
	const formatPasswordErrors = (messages: string[]) =>
		`Password must ${messages
			.map((message) => message.replace(/^Password must /, '').replace(/\.$/, ''))
			.join(', ')}.`;

	useEffect(() => {
		const validate = async () => {
			setValidating(true);
			setTokenValid(null);
			setErrorCode(null);
			try {
				const response = await LoginService.validateResetToken(token);
				if ('error' in response) {
					setTokenValid(false);
					setErrorCode(response.code);
				} else {
					setTokenValid(true);
				}
			} catch (err) {
				console.error(err);
				setTokenValid(false);
				setErrorCode('invalid_token');
			} finally {
				setValidating(false);
			}
		};
		void validate();
	}, [token]);

	const onSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setSubmitting(true);
		setErrorCode(null);
		setFormError(null);
		const validation = AccountService.validatePassword(password);
		if (!validation.valid) {
			setFormError(formatPasswordErrors(validation.failures));
			setSubmitting(false);
			return;
		}
		// eslint-disable-next-line security/detect-possible-timing-attacks -- Client-side password confirmation guard.
		if (password !== confirmPassword) {
			setFormError('Passwords do not match.');
			setSubmitting(false);
			return;
		}
		try {
			const response = await LoginService.resetPassword(token, password);
			if ('error' in response) {
				setErrorCode(response.code);
				return;
			}
			if ('redirect' in response && response.redirect) {
				setRedirecting(true);
				window.location.assign(response.redirect);
				return;
			}
			setFatalError('We could not reset your password.');
			setFormError('We could not reset your password.');
		} catch (err) {
			console.error(err);
			setFatalError('We could not reset your password.');
			setFormError('We could not reset your password.');
		} finally {
			setSubmitting(false);
		}
	};

	if (validating) {
		return (
			<LoginCard>
				<Loading message="Validating" />
			</LoginCard>
		);
	}

	if (tokenValid === false) {
		const errorMessage = errorCode ? resetPasswordErrorCopy[errorCode] : resetPasswordErrorCopy.invalid_token;
		return (
			<LoginCard>
				<h1 className="login-title">Reset password</h1>
				<p className="login-error" role="alert">
					{errorMessage}
				</p>
				<div className="login-footer">
					<Link to="/reset" className="login-link">
						Request a new link
					</Link>
				</div>
			</LoginCard>
		);
	}

	if (redirecting) {
		return (
			<LoginCard>
				<Loading message="Saving new password" />
			</LoginCard>
		);
	}

	const errorMessage = errorCode ? resetPasswordErrorCopy[errorCode] : null;

	return (
		<LoginCard>
			<h1 className="login-title">Create a new password</h1>
			<form className="login-form" onSubmit={onSubmit}>
				<Input
					id="new-password"
					label="New password"
					type="password"
					value={password}
					onChange={(value) => setPassword(value)}
					className="login-field"
					inputProps={{ autoComplete: 'new-password', required: true }}
				/>
				<Input
					id="confirm-password"
					label="Confirm password"
					type="password"
					value={confirmPassword}
					onChange={(value) => setConfirmPassword(value)}
					className="login-field"
					inputProps={{ autoComplete: 'new-password', required: true }}
				/>
				{!formError && <p className="login-description">{passwordRequirementCopy}</p>}
				{formError ? (
					<p className="login-error" role="alert">
						{formError}
					</p>
				) : null}
				{errorMessage ? (
					<p className="login-error" role="alert">
						{errorMessage}
					</p>
				) : null}
				<Button
					className="login-button"
					color="primary"
					size="md"
					variant="contained"
					type="submit"
					disabled={submitting}
				>
					{submitting ? 'Saving...' : 'Save new password'}
				</Button>
			</form>
			<div className="login-footer">
				<Link to="/" className="login-link">
					Return to sign in
				</Link>
			</div>
		</LoginCard>
	);
};
